import React, { useState, useMemo, useEffect } from "react";
import { X, Lock, Save, RotateCcw, AlertTriangle, Users, Cloud, CheckCircle2 } from "lucide-react";
import {
  ADMIN_DEFAULTS,
  MARKETING_DEFAULTS,
  loadAdminState,
  resetAdminState,
  loadAdminPin,
  fetchSharedAdminState,
  saveSharedAdminState,
  fetchPricingAudienceCount,
  mergeAdminStateWithDefaults,
  resolveDayToNightPerKf,
  resolveDayToNightExtraPerKf,
  BASE_ORBIT_KEYFRAMES,
  SUBORBIT_INCLUDED_KEYFRAMES,
  VEXOS_INCLUDED_RENDER_RATE,
} from "./vexosClientPricing";
import { PRICE_OVERRIDE_LINES } from "./priceOverrideLines";

const fmt = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

/* ──────────────────────────────────────────────────────────────────────
 *  VEXOS Pricing Admin — full-page modal overlay
 *  PIN-gated. Persists to localStorage. Mutates the in-memory config
 *  and bumps a version counter (via onSave callback) so the calculator
 *  re-renders with the new values instantly.
 * ─────────────────────────────────────────────────────────────────────── */

function PriceInput({ value, onChange, testId, prefix = "$" }) {
  return (
    <div className="flex items-center bg-white/5 border border-white/10 rounded-md px-2.5 py-1.5 w-36 focus-within:border-[#8FA79B]/50">
      {prefix && <span className="text-white/40 text-[12px] mr-1">{prefix}</span>}
      <input
        type="number"
        value={value === "" || value == null ? "" : value}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "") return onChange(0);
          onChange(Number(v));
        }}
        onFocus={(e) => e.target.select()}
        className="bg-transparent text-white text-[13px] w-full outline-none text-right tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        data-testid={testId}
      />
    </div>
  );
}

function QtyInput({ value, onChange, testId }) {
  return <PriceInput value={value} onChange={onChange} testId={testId} prefix="" />;
}

/**
 * Editable CPL tier list. Each row has a label, an editable upper-bound
 * threshold (last tier is "and above" with no threshold), and an editable
 * $/lead rate. Used by Section 9B (Pre-Sale) and 9C (Pre-Lease).
 */
function CPLEditor({ tiers, onChange, unitLabel = "$", testIdPrefix = "cpl" }) {
  const setTierField = (idx, field, value) => {
    const next = tiers.map((t, i) => (i === idx ? { ...t, [field]: value } : t));
    onChange(next);
  };
  return (
    <div className="space-y-1">
      {tiers.map((t, i) => (
        <div key={t.id} className="flex items-center justify-between gap-3 py-2 border-b border-white/[0.04] last:border-b-0">
          <p className="text-[13px] text-white/85 min-w-[60px]">Tier {i + 1}</p>
          <div className="flex items-center gap-3 flex-1 justify-end">
            {t.upperBound !== null ? (
              <div className="flex items-center gap-1 text-[11px] text-white/55">
                <span>Up to</span>
                <PriceInput
                  value={t.upperBound}
                  onChange={(v) => setTierField(i, "upperBound", v)}
                  testId={`${testIdPrefix}-tier${i + 1}-upper`}
                  prefix={unitLabel}
                />
              </div>
            ) : (
              <span className="text-[11px] text-white/50 italic">and above</span>
            )}
            <div className="flex items-center gap-1 text-[11px] text-white/55">
              <PriceInput
                value={t.rate}
                onChange={(v) => setTierField(i, "rate", v)}
                testId={`${testIdPrefix}-tier${i + 1}-rate`}
              />
              <span className="ml-1">/lead</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function CampaignFeeTiersEditor({ tiers, onChange, testIdPrefix = "admin-mkt-tier" }) {
  const list = Array.isArray(tiers) && tiers.length > 0 ? tiers : [];
  const setTierField = (idx, field, value) => {
    const next = list.map((t, i) => (i === idx ? { ...t, [field]: value } : t));
    onChange(next);
  };
  return (
    <div className="space-y-1">
      {list.map((t, i) => (
        <div key={t.id || `tier${i + 1}`} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 py-2 border-b border-white/[0.04] last:border-b-0">
          <p className="text-[13px] text-white/85">{t.label || `Tier ${i + 1}`}</p>
          {t.upperBound !== null ? (
            <div className="flex items-center gap-1 text-[11px] text-white/55">
              <span>Up to</span>
              <QtyInputCompact
                value={t.upperBound}
                onChange={(v) => setTierField(i, "upperBound", v)}
                testId={`${testIdPrefix}${i + 1}-upper`}
              />
            </div>
          ) : (
            <span className="text-[11px] text-white/50 italic px-1">{list[i - 1]?.upperBound != null ? `${Number(list[i - 1].upperBound) + 1}+ units/lots` : "and above"}</span>
          )}
          <div className="flex items-center gap-1 text-[11px] text-white/55">
            <span>Setup</span>
            <PriceInput
              value={t.setupFee}
              onChange={(v) => setTierField(i, "setupFee", v)}
              testId={`${testIdPrefix}${i + 1}-setup`}
            />
          </div>
          <div className="flex items-center gap-1 text-[11px] text-white/55">
            <PriceInput
              value={t.monthlyServiceFee}
              onChange={(v) => setTierField(i, "monthlyServiceFee", v)}
              testId={`${testIdPrefix}${i + 1}-monthly`}
            />
            <span className="ml-1">/mo</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// Section 8C — one row per manually-overridable price line (the calculator's
// PRICE_OVERRIDE_LINES, same ids + labels): the discount a rep may give freely
// and the floor. List prices are the rate card above, so none are stored here.
function GuardrailLinesEditor({ lines, onChange }) {
  const value = lines || {};
  const setField = (id, field, v) => onChange({ ...value, [id]: { ...(value[id] || {}), [field]: v } });
  return (
    <div className="overflow-hidden rounded-lg border border-white/[0.06]">
      <table className="w-full text-[12px]">
        <thead className="bg-white/[0.03] text-white/55">
          <tr className="text-left">
            <th className="px-3 py-2 font-medium text-[10.5px] uppercase tracking-[0.12em]">Line</th>
            <th className="px-3 py-2 font-medium text-[10.5px] uppercase tracking-[0.12em]">First step (free)</th>
            <th className="px-3 py-2 font-medium text-[10.5px] uppercase tracking-[0.12em]">Floor (max)</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/[0.04]">
          {PRICE_OVERRIDE_LINES.map((l) => {
            const row = value[l.id] || {};
            const bad = Number(row.floorPct) < Number(row.firstStepPct);
            return (
              <tr key={l.id} data-testid={`admin-guardrail-row-${l.id}`}>
                <td className="px-3 py-2 text-white/75">
                  {l.label}
                  <span className="block text-[10px] text-white/30">{l.section}{l.monthly ? " · monthly" : ""}</span>
                </td>
                <td className="px-3 py-2">
                  <PriceInput value={row.firstStepPct} onChange={(v) => setField(l.id, "firstStepPct", v)} testId={`admin-guardrail-${l.id}-step`} prefix="%" />
                </td>
                <td className="px-3 py-2">
                  <PriceInput value={row.floorPct} onChange={(v) => setField(l.id, "floorPct", v)} testId={`admin-guardrail-${l.id}-floor`} prefix="%" />
                  {bad && <span className="block text-[10px] text-amber-300/80 mt-1">Floor is below the first step</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Section 8D — the complimentary add-on list. Editable label + value per
// row, add / remove rows. `value` is per unit, what the add-on would bill.
function FreeAddOnsEditor({ items, onChange }) {
  const list = Array.isArray(items) ? items : [];
  const setField = (idx, field, v) => onChange(list.map((a, i) => (i === idx ? { ...a, [field]: v } : a)));
  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));
  const add = () => {
    const taken = new Set(list.map((a) => a.id));
    let n = list.length + 1;
    while (taken.has(`addon${n}`)) n += 1;
    onChange([...list, { id: `addon${n}`, label: "New add-on", value: 0 }]);
  };
  return (
    <div>
      <div className="space-y-1">
        {list.map((a, i) => (
          <div key={a.id || i} className="grid grid-cols-[1fr_auto_auto] items-center gap-3 py-2 border-b border-white/[0.04] last:border-b-0" data-testid={`admin-free-addon-row-${i}`}>
            <input
              type="text"
              value={a.label || ""}
              onChange={(e) => setField(i, "label", e.target.value)}
              className="bg-white/5 border border-white/10 rounded-md px-2.5 py-1.5 text-white text-[13px] outline-none focus:border-[#8FA79B]/50"
              data-testid={`admin-free-addon-${i}-label`}
            />
            <PriceInput value={a.value} onChange={(v) => setField(i, "value", v)} testId={`admin-free-addon-${i}-value`} />
            <button
              type="button"
              onClick={() => remove(i)}
              className="p-1.5 rounded text-white/35 hover:text-red-300 transition-colors"
              title="Remove"
              data-testid={`admin-free-addon-${i}-remove`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        className="mt-3 px-3 py-1.5 rounded-md text-[11px] uppercase tracking-[0.12em] text-white/65 hover:text-white border border-white/15 hover:border-white/30 transition-colors"
        data-testid="admin-free-addon-add"
      >
        + Add an add-on
      </button>
    </div>
  );
}

// Compact integer input — same visual language as PriceInput but no `$`
// prefix. Used for unit/lot count thresholds in the campaign-fee tier table.
function QtyInputCompact({ value, onChange, testId }) {
  return (
    <input
      type="number"
      inputMode="numeric"
      value={value ?? ""}
      onChange={(e) => onChange(Number(e.target.value) || 0)}
      data-testid={testId}
      className="bg-white/[0.04] border border-white/10 rounded-md text-white text-right text-[12px] tabular-nums px-2 py-1 w-[72px] focus:outline-none focus:border-[#8FA79B]/60 transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
    />
  );
}

function Row({ label, hint, children }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5 border-b border-white/[0.04] last:border-b-0">
      <div className="flex-1 min-w-0">        <p className="text-[13px] text-white/85">{label}</p>
        {hint && <p className="text-[10.5px] text-white/35 mt-0.5">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

function SectionCard({ title, subtitle, children }) {
  return (
    <section className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-6 mb-5">
      <header className="mb-4 pb-3 border-b border-white/[0.05]">
        <h2 className="text-[15px] text-white tracking-tight" style={{ fontFamily: 'var(--font-sans)', fontWeight: 450 }}>{title}</h2>
        {subtitle && <p className="text-[11px] text-white/40 mt-0.5">{subtitle}</p>}
      </header>
      {children}
    </section>
  );
}

function VolumeTierEditor({ section, value, onChange, qtyUnit }) {
  // value: { tier1MaxQty, tier1Rate, tier2MaxQty, tier2Rate, tier3Rate }
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div>
      <div className="overflow-hidden rounded-lg border border-white/[0.06]">
        <table className="w-full text-[12px]">
          <thead className="bg-white/[0.03] text-white/55">
            <tr className="text-left">
              <th className="px-3 py-2 font-medium text-[10.5px] uppercase tracking-[0.12em]">Tier</th>
              <th className="px-3 py-2 font-medium text-[10.5px] uppercase tracking-[0.12em]">Volume Range</th>
              <th className="px-3 py-2 font-medium text-[10.5px] uppercase tracking-[0.12em]">Price per {qtyUnit === "points" ? "Point" : "Unit"}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.04]">
            <tr>
              <td className="px-3 py-3 text-white/70">Standard</td>
              <td className="px-3 py-3 text-white/60">
                1 – <span className="inline-block align-middle"><QtyInput value={value.tier1MaxQty} onChange={(v) => set("tier1MaxQty", v)} testId={`admin-${section}-tier1-max`} /></span> <span className="text-white/40 ml-1">{qtyUnit}</span>
              </td>
              <td className="px-3 py-3"><PriceInput value={value.tier1Rate} onChange={(v) => set("tier1Rate", v)} testId={`admin-${section}-tier1-rate`} /></td>
            </tr>
            <tr>
              <td className="px-3 py-3 text-white/70">Volume</td>
              <td className="px-3 py-3 text-white/60">
                {(Number(value.tier1MaxQty) || 0) + 1} – <span className="inline-block align-middle"><QtyInput value={value.tier2MaxQty} onChange={(v) => set("tier2MaxQty", v)} testId={`admin-${section}-tier2-max`} /></span> <span className="text-white/40 ml-1">{qtyUnit}</span>
              </td>
              <td className="px-3 py-3"><PriceInput value={value.tier2Rate} onChange={(v) => set("tier2Rate", v)} testId={`admin-${section}-tier2-rate`} /></td>
            </tr>
            <tr>
              <td className="px-3 py-3 text-white/70">Scale</td>
              <td className="px-3 py-3 text-white/60">{(Number(value.tier2MaxQty) || 0) + 1}+ {qtyUnit}</td>
              <td className="px-3 py-3"><PriceInput value={value.tier3Rate} onChange={(v) => set("tier3Rate", v)} testId={`admin-${section}-tier3-rate`} /></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PinGate({ onUnlock, onClose }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);
  const submit = (e) => {
    e?.preventDefault();
    if (pin === loadAdminPin()) onUnlock();
    else { setError(true); setPin(""); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#000000]">
      <form onSubmit={submit} className="rounded-2xl border border-white/[0.08] bg-[#000000] p-10 max-w-sm w-full text-center shadow-[0_0_60px_rgba(0,0,0,0.5)]">
        <Lock className="w-7 h-7 text-[#8FA79B] mx-auto mb-4" />
        <h2 className="text-[22px] text-white mb-1" style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}>Pricing Admin</h2>
        <p className="text-[11.5px] text-white/45 mb-6">Enter the 4-digit access PIN.</p>
        <input
          type="password"
          inputMode="numeric"
          maxLength={4}
          value={pin}
          onChange={(e) => { setPin(e.target.value.replace(/[^0-9]/g, "").slice(0, 4)); setError(false); }}
          className="w-full text-center tracking-[0.5em] text-[24px] text-white bg-white/[0.03] border border-white/10 rounded-lg py-3 mb-3 outline-none focus:border-[#8FA79B]/50 tabular-nums"
          autoFocus
          data-testid="admin-pin-input"
        />
        {error && <p className="text-[11px] text-red-400/80 mb-3 flex items-center justify-center gap-1.5"><AlertTriangle className="w-3 h-3" /> Incorrect PIN.</p>}
        <button type="submit" className="w-full py-3 bg-[#8FA79B] hover:bg-[#8FA79B]/90 text-[#000000] rounded-lg font-medium text-[13px] uppercase tracking-[0.12em] transition-colors" data-testid="admin-pin-submit">
          Unlock
        </button>
        <button type="button" onClick={onClose} className="mt-3 text-[11px] uppercase tracking-[0.12em] text-white/40 hover:text-white/70 transition-colors" data-testid="admin-pin-cancel">
          Cancel
        </button>
        <p className="text-[10px] text-white/25 mt-6">Default PIN is <code className="bg-white/5 px-1 rounded">0000</code>. Change it in <code className="bg-white/5 px-1 rounded">vexosClientPricing.js</code> via <code className="bg-white/5 px-1 rounded">saveAdminPin()</code>.</p>
      </form>
    </div>
  );
}

export default function VexosPricingAdmin({ onClose, onSaved, initialMeta, initialAudienceCount }) {
  const [unlocked, setUnlocked] = useState(false);
  const [state, setState] = useState(() => loadAdminState());
  const [baseline, setBaseline] = useState(() => loadAdminState()); // synced after each successful save
  const [dirty, setDirty] = useState(false);
  const [bundlePreviewBase, setBundlePreviewBase] = useState(100000);

  // Shared backend metadata (last update audit + audience size for save chip)
  const [meta, setMeta] = useState(initialMeta || { exists: false, updatedAt: null, updatedByName: null });
  const [audienceCount, setAudienceCount] = useState(initialAudienceCount || 0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [justSaved, setJustSaved] = useState(false);

  // Hydrate from backend once the PIN is unlocked (or skip-PIN if needed).
  useEffect(() => {
    let mounted = true;
    (async () => {
      const [shared, count] = await Promise.all([
        fetchSharedAdminState(),
        fetchPricingAudienceCount(),
      ]);
      if (!mounted) return;
      setState(shared.state);
      setBaseline(shared.state);
      setMeta(shared.meta);
      setAudienceCount(count);
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    setDirty(JSON.stringify(state) !== JSON.stringify(baseline));
  }, [state, baseline]);

  const update = (section, patch) => setState((s) => ({ ...s, [section]: { ...s[section], ...patch } }));

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const newMeta = await saveSharedAdminState(state);
      setMeta(newMeta);
      setBaseline(state);
      setDirty(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2500);
      onSaved?.({ state, meta: newMeta });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to save pricing.";
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset all pricing to defaults and push to ALL team members? This cannot be undone.")) return;
    const defaults = JSON.parse(JSON.stringify(ADMIN_DEFAULTS));
    setState(defaults);
    setSaving(true);
    setSaveError(null);
    try {
      const newMeta = await saveSharedAdminState(defaults);
      setMeta(newMeta);
      setBaseline(defaults);
      setDirty(false);
      resetAdminState();
      onSaved?.({ state: defaults, meta: newMeta });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to reset pricing.";
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!unlocked) {
    return (
      <div className="fixed inset-0 z-[80] bg-[#000000]" style={{ fontFamily: 'var(--font-sans)' }}>
        <PinGate onUnlock={() => setUnlocked(true)} onClose={onClose} />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[80] bg-[#000000] overflow-y-auto" style={{ fontFamily: 'var(--font-sans)' }} data-testid="pricing-admin-panel">
      <div className="max-w-5xl mx-auto px-8 lg:px-12 py-10 pb-32">
        {/* Header */}
        <header className="flex items-start justify-between mb-10">
          <div>
            <p className="text-[10.5px] uppercase tracking-[0.24em] text-[#8FA79B]/80 mb-2">Internal · Admin</p>
            <h1 className="text-[40px] text-white leading-none tracking-tight" style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}>Pricing Configuration</h1>
            <p className="text-[13px] text-white/45 mt-3 max-w-xl">Changes apply immediately to the live calculator. Volume-tier rates determine which per-unit price is applied to the entire order based on the total quantity.</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors p-2" data-testid="admin-close">
            <X className="w-5 h-5" />
          </button>
        </header>

        {/* Shared-config audit banner */}
        <div
          className="mb-8 rounded-lg border border-[#8FA79B]/20 bg-[#8FA79B]/[0.04] px-4 py-3 flex items-start gap-3"
          data-testid="pricing-admin-meta-banner"
        >
          <Cloud className="w-4 h-4 text-[#8FA79B] mt-0.5 shrink-0" />
          <div className="flex-1 text-[12px] text-white/75 leading-relaxed">
            {meta?.exists ? (
              <>
                <span className="text-white/90 font-medium">Shared with all team members.</span>{" "}
                Last updated by{" "}
                <span className="text-[#8FA79B]">{meta.updatedByName || "—"}</span>
                {meta.updatedAt && (
                  <>
                    {" "}on{" "}
                    <span className="tabular-nums text-white/70" data-testid="pricing-admin-last-updated">
                      {new Date(meta.updatedAt).toLocaleString()}
                    </span>
                  </>
                )}
                .
              </>
            ) : (
              <>
                <span className="text-amber-200/90">No shared pricing saved yet.</span>{" "}
                Saving will push these values to every team member instantly.
              </>
            )}
          </div>
          <div className="inline-flex items-center gap-1.5 text-[11px] text-white/60 shrink-0 mt-0.5" data-testid="pricing-admin-audience">
            <Users className="w-3 h-3" />
            <span className="tabular-nums">{audienceCount}</span>
            <span>active</span>
          </div>
        </div>

        {/* SECTION 1 — VEXOS PLATFORM FEES */}
        <SectionCard title="1 · VEXOS Platform Fees" subtitle="Foundational platform fees shared across Condo Building and Community calculators.">
          <div className="mb-5">
            <p className="text-[11px] uppercase tracking-[0.14em] text-white/55 mb-3">1A · One-Time Setup Fee — tiered by unit/lot count</p>
            <Row label="Tier 1 — Standard" hint={`Under ${(Number(state.setup.tier1MaxUnits) || 0) + 1} units / lots`}>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-white/40">Up to</span>
                <QtyInput value={state.setup.tier1MaxUnits} onChange={(v) => update("setup", { tier1MaxUnits: v })} testId="admin-setup-tier1-max" />
                <PriceInput value={state.setup.tier1Fee} onChange={(v) => update("setup", { tier1Fee: v })} testId="admin-setup-tier1-fee" />
              </div>
            </Row>
            <Row label="Tier 2 — Volume" hint={`${(Number(state.setup.tier1MaxUnits) || 0) + 1} – ${state.setup.tier2MaxUnits} units / lots`}>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-white/40">Up to</span>
                <QtyInput value={state.setup.tier2MaxUnits} onChange={(v) => update("setup", { tier2MaxUnits: v })} testId="admin-setup-tier2-max" />
                <PriceInput value={state.setup.tier2Fee} onChange={(v) => update("setup", { tier2Fee: v })} testId="admin-setup-tier2-fee" />
              </div>
            </Row>
            <Row label="Tier 3 — Scale" hint={`${(Number(state.setup.tier2MaxUnits) || 0) + 1}+ units / lots`}>
              <PriceInput value={state.setup.tier3Fee} onChange={(v) => update("setup", { tier3Fee: v })} testId="admin-setup-tier3-fee" />
            </Row>
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-[0.14em] text-white/55 mb-3">1B · Monthly Subscription Rates — by Scale Tier and Term Length</p>
            <div className="overflow-hidden rounded-lg border border-white/[0.06]">
              <table className="w-full text-[12px]">
                <thead className="bg-white/[0.03] text-white/55">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-medium text-[10.5px] uppercase tracking-[0.12em]">Tier</th>
                    <th className="px-3 py-2 font-medium text-[10.5px] uppercase tracking-[0.12em]">Scale</th>
                    <th className="px-3 py-2 font-medium text-[10.5px] uppercase tracking-[0.12em]">12 mo</th>
                    <th className="px-3 py-2 font-medium text-[10.5px] uppercase tracking-[0.12em]">24 mo</th>
                    <th className="px-3 py-2 font-medium text-[10.5px] uppercase tracking-[0.12em]">36 mo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.04]">
                  {[
                    { key: "tier1", label: "Standard", scale: `Under ${(Number(state.monthly.tier1MaxUnits) || 0) + 1}` },
                    { key: "tier2", label: "Volume",   scale: `${(Number(state.monthly.tier1MaxUnits) || 0) + 1} – ${state.monthly.tier2MaxUnits}` },
                    { key: "tier3", label: "Scale",    scale: `${(Number(state.monthly.tier2MaxUnits) || 0) + 1}+` },
                  ].map(t => (
                    <tr key={t.key}>
                      <td className="px-3 py-3 text-white/70">{t.label}</td>
                      <td className="px-3 py-3 text-white/50 text-[11px]">{t.scale}</td>
                      {[12, 24, 36].map(term => (
                        <td key={term} className="px-3 py-3">
                          <PriceInput
                            value={state.monthly.rates[t.key][term]}
                            onChange={(v) => update("monthly", { rates: { ...state.monthly.rates, [t.key]: { ...state.monthly.rates[t.key], [term]: v } } })}
                            testId={`admin-monthly-${t.key}-${term}`}
                          />
                          <div className="text-[10px] text-white/35 mt-1 tabular-nums text-right">
                            ×{term} = {fmt(state.monthly.rates[t.key][term] * term)}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[10.5px] text-white/35 italic mt-3">Tier thresholds inherit from Setup Fee. Longer commitments are typically discounted.</p>
          </div>
        </SectionCard>

        {/* SECTION 1S — SFH (Single-Family Home) FIXED PRICING */}
        <SectionCard
          title="1S · SFH (Single-Family Home) — Fixed Pricing"
          subtitle="Flat-rate VEXOS platform offer for SFH engagements. No tiers, no unit/floor logic — the calculator emits these exact numbers whenever Project Type = SFH."
        >
          <Row label="One-Time Setup Fee" hint="Flat price for the entire SFH engagement.">
            <PriceInput
              value={state.sfh?.setupFee ?? ADMIN_DEFAULTS.sfh.setupFee}
              onChange={(v) => update("sfh", { setupFee: v })}
              testId="admin-sfh-setup-fee"
            />
          </Row>
          <Row label="Monthly Subscription" hint="Flat monthly subscription billed for the duration of the SFH contract.">
            <PriceInput
              value={state.sfh?.monthlySubscription ?? ADMIN_DEFAULTS.sfh.monthlySubscription}
              onChange={(v) => update("sfh", { monthlySubscription: v })}
              testId="admin-sfh-monthly"
            />
          </Row>
          <p className="text-[10.5px] text-white/40 italic mt-3">
            SFH pricing is completely independent of Condo and Community tiers. Existing Condo / Community quotes are unaffected by changes here.
          </p>
        </SectionCard>

        {/* SECTION 1M — MASTER PLAN SETUP FEE */}
        <SectionCard
          title="1M · Master Plan — Setup Fee"
          subtitle="Flat setup fee for Master Plan quotes (a community with a master orbit and two included suborbits). The monthly still follows the unit tiers above."
        >
          <Row label="One-Time Setup Fee" hint="Replaces the community setup-fee tier whenever Project Type = Master Plan.">
            <PriceInput
              value={state.masterPlan?.setupFee ?? ADMIN_DEFAULTS.masterPlan.setupFee}
              onChange={(v) => update("masterPlan", { setupFee: v })}
              testId="admin-master-plan-setup-fee"
            />
          </Row>
        </SectionCard>

        {/* SECTION 2 — EXTERIOR ADD-ONS */}
        <SectionCard title="2 · Exterior Add-Ons" subtitle="Applied to both Condo Building and Community.">
          <Row
            label="Day-to-Night — included keyframes"
            hint={`Priced 1:1 with the keyframes an orbit includes, so the figure reads off the orbit: the ${BASE_ORBIT_KEYFRAMES}-keyframe base orbit is ${BASE_ORBIT_KEYFRAMES} × this rate, and a ${SUBORBIT_INCLUDED_KEYFRAMES}-keyframe suborbit is ${SUBORBIT_INCLUDED_KEYFRAMES} × it.`}
          >
            <div className="flex items-center gap-2">
              <PriceInput
                value={resolveDayToNightPerKf(state.exterior)}
                onChange={(v) => update("exterior", { dayToNightPerKf: v })}
                testId="admin-ext-d2n"
              />
              <span className="text-[11px] text-white/40">/ included keyframe</span>
            </div>
          </Row>
          <Row
            label="Day-to-Night — added keyframes"
            hint="Keyframes added to an orbit beyond the ones it includes. Half the included rate by default."
          >
            <div className="flex items-center gap-2">
              <PriceInput
                value={resolveDayToNightExtraPerKf(state.exterior)}
                onChange={(v) => update("exterior", { dayToNightExtraPerKf: v })}
                testId="admin-ext-d2n-extra"
              />
              <span className="text-[11px] text-white/40">/ added keyframe</span>
            </div>
          </Row>
          {/* What those two rates actually produce, so nobody has to do the
              arithmetic to sanity-check a change. */}
          <Row label="" hint="">
            <p className="text-[11px] text-white/40 leading-snug">
              Base orbit {fmt(BASE_ORBIT_KEYFRAMES * resolveDayToNightPerKf(state.exterior))}
              {" · "}each suborbit {fmt(SUBORBIT_INCLUDED_KEYFRAMES * resolveDayToNightPerKf(state.exterior))}
              {" · "}each added keyframe {fmt(resolveDayToNightExtraPerKf(state.exterior))}
            </p>
          </Row>
          <Row label="Additional Keyframes" hint="Price per keyframe">
            <div className="flex items-center gap-2">
              <PriceInput value={state.exterior.additionalKeyframePerKf} onChange={(v) => update("exterior", { additionalKeyframePerKf: v })} testId="admin-ext-add-kf" />
              <span className="text-[11px] text-white/40">/ keyframe</span>
            </div>
          </Row>
          <Row label="Animated Keyframes" hint="Price per keyframe animated">
            <div className="flex items-center gap-2">
              <PriceInput value={state.exterior.animatedKeyframePerKf} onChange={(v) => update("exterior", { animatedKeyframePerKf: v })} testId="admin-ext-anim-kf" />
              <span className="text-[11px] text-white/40">/ keyframe animated</span>
            </div>
          </Row>
          <Row label="Additional Orbit" hint="Price per suborbit (max 5 per project). Each suborbit includes two keyframes; extras bill at the Additional Keyframes rate above.">
            <div className="flex items-center gap-2">
              <PriceInput value={state.exterior.masterOrbitPerOrbit} onChange={(v) => update("exterior", { masterOrbitPerOrbit: v })} testId="admin-ext-master-orbit" />
              <span className="text-[11px] text-white/40">/ orbit</span>
            </div>
          </Row>
          <Row label="VEXOS Cover Landing Page" hint="Branded project landing page hosted within VEXOS. One-time flat fee.">
            <div className="flex items-center gap-2">
              <PriceInput value={state.exterior.vexosCoverLandingPage ?? 2500} onChange={(v) => update("exterior", { vexosCoverLandingPage: v })} testId="admin-ext-vexos-cover-lp" />
              <span className="text-[11px] text-white/40">· one-time</span>
            </div>
          </Row>
          <Row
            label="Conceptual Rendering Hybrid Workflow"
            hint="VEXOS + À La Carte. Flat per-image rate: the unit is fully modeled, with AI assisting the design, materials and finishes. Not pooled with the photoreal render volume tiers."
          >
            <div className="flex items-center gap-2">
              <PriceInput
                value={state.conceptualHybrid?.pricePerRender ?? ADMIN_DEFAULTS.conceptualHybrid.pricePerRender}
                onChange={(v) => update("conceptualHybrid", { pricePerRender: v })}
                testId="admin-conceptual-per-render"
              />
              <span className="text-[11px] text-white/40">/ image</span>
            </div>
          </Row>
        </SectionCard>

        {/* SECTION 3 — INTERIOR RENDERINGS (STANDALONE RATE CARD) */}
        <SectionCard title="3 · Interior Renderings — Volume Tiers (Standalone)" subtitle="The À La Carte rate card, used when visualization is sold on its own with no VEXOS platform. Tiered by TOTAL render count across all floor plans / home models — the qualifying tier rate applies to the entire order.">
          <VolumeTierEditor section="int-render" value={state.interiorRendering} onChange={(v) => update("interiorRendering", v)} qtyUnit="renders" />
          <p className="mt-3 text-[11.5px] text-[#8FA79B]/75 italic">
            Note: Exterior renderings use the same volume tier rates. Exterior and interior render counts are combined when determining the qualifying tier.
          </p>
          <p className="mt-2 text-[11.5px] text-white/40 italic">
            Renderings sold inside a VEXOS engagement bill off Section 3B instead.
          </p>
        </SectionCard>

        {/* SECTION 3B — VEXOS-INCLUDED RENDERING RATE CARD */}
        <SectionCard
          title="3B · Renderings Included With VEXOS — Volume Tiers"
          subtitle={`The second rendering rate card. Every rendering in a VEXOS engagement — interior / home model, exterior, and amenity — bills off these tiers instead of the standalone tables. Seeded at a flat ${fmt(VEXOS_INCLUDED_RENDER_RATE)} per rendering — the discount VEXOS unlocks; edit freely from here.`}
        >
          <div className="mb-5">
            <p className="text-[11px] uppercase tracking-[0.14em] text-white/55 mb-3">3B-1 · Interior / Home Model + Exterior Renderings</p>
            <VolumeTierEditor
              section="vexos-int-render"
              value={state.vexosInteriorRendering}
              onChange={(v) => update("vexosInteriorRendering", v)}
              qtyUnit="renders"
            />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.14em] text-white/55 mb-3">3B-2 · Amenity Renderings</p>
            <VolumeTierEditor
              section="vexos-am-render"
              value={state.vexosAmenityRendering}
              onChange={(v) => update("vexosAmenityRendering", v)}
              qtyUnit="renders"
            />
          </div>
          <p className="mt-3 text-[11.5px] text-white/40 italic">
            VR tours, floor plans, video and exterior add-ons have a single rate card each — only renderings are priced differently inside VEXOS.
          </p>
        </SectionCard>

        {/* SECTION 4 — INTERIOR VR TOURS */}
        <SectionCard title="4 · Interior VR Tours — Volume Tiers" subtitle="Tiered by TOTAL VR points across all floor plans / home models. The qualifying tier rate applies to all selected points.">
          <VolumeTierEditor section="int-vr" value={state.interiorVR} onChange={(v) => update("interiorVR", v)} qtyUnit="points" />
        </SectionCard>

        {/* SECTION 4B — EXTERIOR VR TOUR */}
        <SectionCard title="4B · Exterior VR Tour — Volume Tiers" subtitle="Single per-project tour around the building/community exterior (street-view style). Tiered by the tour's point count; applies to Condo, SFH & Community.">
          <VolumeTierEditor section="ext-vr" value={state.exteriorVR} onChange={(v) => update("exteriorVR", v)} qtyUnit="points" />
        </SectionCard>

        {/* SECTION 4C — DRONE FEES */}
        <SectionCard title="4C · Drone Fees" subtitle="Flat per-project rates. Capture fee auto-applies whenever any VR tour is active; shot views are optional creative add-ons per VR section.">
          <Row label="Drone & Reality Capture (auto-applied)" hint="One-time per project. Auto-triggers when any VR tour (Interior or Amenity) is selected.">
            <PriceInput
              value={state.drone?.captureFee ?? 5000}
              onChange={(v) => update("drone", { ...(state.drone || {}), captureFee: v })}
              testId="admin-drone-capture"
            />
          </Row>
          <Row label="Drone Shot Views — Interior" hint="Optional add-on per project for interior VR sections.">
            <PriceInput
              value={state.drone?.interiorDroneShotsFee ?? 5000}
              onChange={(v) => update("drone", { ...(state.drone || {}), interiorDroneShotsFee: v })}
              testId="admin-drone-int-shots"
            />
          </Row>
          <Row label="Drone Shot Views — Amenity" hint="Optional add-on per project for amenity VR sections.">
            <PriceInput
              value={state.drone?.amenityDroneShotsFee ?? 5000}
              onChange={(v) => update("drone", { ...(state.drone || {}), amenityDroneShotsFee: v })}
              testId="admin-drone-am-shots"
            />
          </Row>
        </SectionCard>

        {/* SECTION 4D — REAL-TIME CONFIGURATOR */}
        <SectionCard
          title="4D · Real-Time Configurator"
          subtitle="VR tour add-on: the buyer switches finishes, materials and furniture inside the tour. One activation fee per VR section (residences, amenities). Standard categories price each option in order: the 1st (included in the activation at $0), the 2nd, then each one after. Furniture packages bill every option at the rate their count earns."
        >
          {[
            ["activationFee", "Activation fee", "/ VR section"],
            ["option1", "Standard category · 1st option", "($0 = included in activation)"],
            ["option2", "Standard category · 2nd option", "added"],
            ["option3Plus", "Standard category · each option after", "added, each"],
            ["furniture1", "Furniture package · 1 option", "/ option"],
            ["furniture2", "Furniture package · 2 options", "/ option"],
            ["furniture3", "Furniture package · 3 options", "/ option"],
            ["furniture4Plus", "Furniture package · 4+ options", "/ option"],
          ].map(([key, label, unit]) => (
            <Row key={key} label={label}>
              <div className="flex items-center gap-2">
                <PriceInput
                  value={state.vrConfigurator?.[key] ?? ADMIN_DEFAULTS.vrConfigurator[key]}
                  onChange={(v) => update("vrConfigurator", { [key]: v })}
                  testId={`admin-vr-configurator-${key}`}
                />
                <span className="text-[11px] text-white/40">{unit}</span>
              </div>
            </Row>
          ))}
        </SectionCard>

        {/* SECTION 5 — AMENITY EXPERIENCE */}
        <SectionCard title="5 · Amenity Experience Pricing">
          <div className="mb-5">
            <p className="text-[11px] uppercase tracking-[0.14em] text-white/55 mb-3">5A · Amenity Renderings — Volume Tiers (Standalone)</p>
            <p className="text-[10.5px] text-white/35 italic mb-3">Standalone / À La Carte rate card. Amenity renderings inside a VEXOS engagement bill off Section 3B-2.</p>
            <VolumeTierEditor section="am-render" value={state.amenityRendering} onChange={(v) => update("amenityRendering", v)} qtyUnit="renders" />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.14em] text-white/55 mb-3">5B · Amenity VR Tour — Package Pricing</p>
            <p className="text-[10.5px] text-white/35 italic mb-3">Each amenity space picks one package (2 / 4 / 6 / 8 / 10 points). Flat price per row.</p>
            <div className="overflow-hidden rounded-lg border border-white/[0.06]">
              <table className="w-full text-[12px]">
                <thead className="bg-white/[0.03] text-white/55">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-medium text-[10.5px] uppercase tracking-[0.12em]">Package</th>
                    <th className="px-3 py-2 font-medium text-[10.5px] uppercase tracking-[0.12em]">Points</th>
                    <th className="px-3 py-2 font-medium text-[10.5px] uppercase tracking-[0.12em]">Price (flat)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.04]">
                  {[2, 4, 6, 8, 10].map(pts => (
                    <tr key={pts}>
                      <td className="px-3 py-3 text-white/70">{pts}-Point Tour</td>
                      <td className="px-3 py-3 text-white/50 tabular-nums">{pts}</td>
                      <td className="px-3 py-3">
                        <PriceInput
                          value={state.amenityVRPackages[pts]}
                          onChange={(v) => update("amenityVRPackages", { ...state.amenityVRPackages, [pts]: v })}
                          testId={`admin-am-vr-pkg-${pts}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </SectionCard>

        {/* SECTION 6 — BUNDLE INCENTIVE */}
        <SectionCard title="6 · Bundle Incentive Configuration" subtitle="Offered to the rep when a quote spans multiple experience modules — never added automatically. The calculator shows the tier the quote qualifies for, and the discount lands on the quote and proposal only when the rep applies it. Applies to Studio Visualization subtotal only.">
          {/* 6A — Master toggle */}
          <Row label="Bundle Discounts Active" hint="Master ON/OFF for the entire bundle system. Off: no quote qualifies, so reps see no bundle to apply.">
            <button
              type="button"
              onClick={() => update("bundle", { enabled: !state.bundle.enabled })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${state.bundle.enabled ? "bg-[#8FA79B]" : "bg-white/15"}`}
              data-testid="admin-bundle-toggle"
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${state.bundle.enabled ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </Row>

          {/* 6B — Bundle tier rates */}
          <div className="mt-5">
            <p className="text-[11px] uppercase tracking-[0.14em] text-white/55 mb-3">6B · Bundle Tier Discount Rates</p>
            <div className="overflow-hidden rounded-lg border border-white/[0.06]">
              <table className="w-full text-[12px]">
                <thead className="bg-white/[0.03] text-white/55">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-medium text-[10.5px] uppercase tracking-[0.12em]">Bundle Tier</th>
                    <th className="px-3 py-2 font-medium text-[10.5px] uppercase tracking-[0.12em]">Modules Required</th>
                    <th className="px-3 py-2 font-medium text-[10.5px] uppercase tracking-[0.12em]">Discount %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.04]">
                  {[
                    { key: "dualPct",      label: "Dual Experience Bundle",   modules: "2 of 6 modules" },
                    { key: "triplePct",    label: "Triple Experience Bundle", modules: "3 of 6 modules" },
                    { key: "fullStackPct", label: "Full Stack Bundle",        modules: "5 of 6 modules" },
                  ].map(b => (
                    <tr key={b.key}>
                      <td className="px-3 py-3 text-white/70">{b.label}</td>
                      <td className="px-3 py-3 text-white/50 text-[11px]">{b.modules}</td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1.5">
                          <PriceInput
                            value={state.bundle[b.key]}
                            onChange={(v) => update("bundle", { [b.key]: v })}
                            testId={`admin-bundle-${b.key}`}
                            prefix=""
                          />
                          <span className="text-[11px] text-white/40">%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 6C — Scope note */}
          <p className="text-[11px] text-white/45 italic mt-5 leading-snug">
            6C · Bundle discounts apply to Studio Visualization subtotal only. VEXOS Platform fees (setup + monthly subscription) are excluded from bundle pricing.
          </p>

          {/* 6D — Live preview */}
          <div className="mt-5 rounded-lg bg-white/[0.02] border border-white/[0.06] p-4">
            <p className="text-[10.5px] uppercase tracking-[0.14em] text-white/55 mb-3">6D · Live Bundle Preview</p>
            <div className="flex items-center gap-3 mb-3">
              <span className="text-[12px] text-white/65">Hypothetical Visualization Subtotal:</span>
              <PriceInput value={bundlePreviewBase} onChange={setBundlePreviewBase} testId="admin-bundle-preview-base" />
            </div>
            <div className="space-y-1.5 text-[12px]">
              {[
                { label: "Dual Experience Bundle",   pct: state.bundle.dualPct },
                { label: "Triple Experience Bundle", pct: state.bundle.triplePct },
                { label: "Full Stack Bundle",        pct: state.bundle.fullStackPct },
              ].map(b => {
                const saves = Math.round((bundlePreviewBase || 0) * (Number(b.pct) || 0) / 100);
                const net = (bundlePreviewBase || 0) - saves;
                return (
                  <div key={b.label} className="flex items-center justify-between py-1.5 border-b border-white/[0.04] last:border-b-0">
                    <span className="text-white/65">{b.label} ({b.pct}%)</span>
                    <span className="tabular-nums text-white/85">saves <span className="text-emerald-300/85">{fmt(saves)}</span> → net {fmt(net)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </SectionCard>

        {/* SECTION 7 — VIDEO ANIMATION */}
        <SectionCard title="7 · Video Animation" subtitle="Add-on per-duration prices (AI Motion + Cinematic) plus standalone video tiers used when a video is sold on its own.">
          <div className="mb-5">
            <p className="text-[11px] uppercase tracking-[0.14em] text-white/55 mb-3">7A · AI Motion Pricing</p>
            {[30, 60, 90].map(sec => (
              <Row key={sec} label={`${sec} seconds`}>
                <PriceInput
                  value={state.videoAnimation?.aiMotion?.[sec] ?? 0}
                  onChange={(v) => update("videoAnimation", { aiMotion: { ...(state.videoAnimation?.aiMotion || {}), [sec]: v } })}
                  testId={`admin-video-ai-${sec}`}
                />
              </Row>
            ))}
          </div>
          <div className="mb-5">
            <p className="text-[11px] uppercase tracking-[0.14em] text-white/55 mb-3">7B · Cinematic Animation Pricing</p>
            {[30, 60, 90].map(sec => (
              <Row key={sec} label={`${sec} seconds`}>
                <PriceInput
                  value={state.videoAnimation?.cinematic?.[sec] ?? 0}
                  onChange={(v) => update("videoAnimation", { cinematic: { ...(state.videoAnimation?.cinematic || {}), [sec]: v } })}
                  testId={`admin-video-cine-${sec}`}
                />
              </Row>
            ))}
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.14em] text-white/55 mb-3">7C · Motion Package Discount</p>
            <Row label="Motion Package enabled" hint="Applied when both AI Motion and Cinematic Animation are selected simultaneously. Discount applies to combined video subtotal only.">
              <button
                type="button"
                onClick={() => update("videoAnimation", { motionPackage: { ...(state.videoAnimation?.motionPackage || {}), enabled: !(state.videoAnimation?.motionPackage?.enabled !== false) } })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${state.videoAnimation?.motionPackage?.enabled !== false ? "bg-[#8FA79B]" : "bg-white/15"}`}
                data-testid="admin-motion-package-toggle"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${state.videoAnimation?.motionPackage?.enabled !== false ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </Row>
            <Row label="Motion Package discount %">
              <div className="flex items-center gap-1.5">
                <PriceInput
                  value={state.videoAnimation?.motionPackage?.pct ?? 10}
                  onChange={(v) => update("videoAnimation", { motionPackage: { ...(state.videoAnimation?.motionPackage || {}), pct: v } })}
                  testId="admin-motion-package-pct"
                  prefix=""
                />
                <span className="text-[11px] text-white/40">%</span>
              </div>
            </Row>
          </div>
          <div className="mt-6 pt-5 border-t border-white/[0.06]">
            <p className="text-[11px] uppercase tracking-[0.14em] text-white/55 mb-1">7D · Standalone Video Pricing (size-first)</p>
            <p className="text-[10.5px] text-white/35 mb-3">
              Used only when a video is sold on its own (À La Carte, no Vexos package). Project size dictates the
              format offered: Small = 30s hero · Medium = 60s hero · Large = 90s hero or 2-minute master (Large also
              unlocks the add-on menu). Social cut-downs are an editing line item, never priced as animation.
            </p>
            <div className="mb-5">
              <p className="text-[10.5px] uppercase tracking-[0.12em] text-white/40 mb-2">Hero / master prices</p>
              {[
                { key: "hero30", label: "Small · 30-second hero" },
                { key: "hero60", label: "Medium · 60-second hero" },
                { key: "hero90", label: "Large · 90-second hero" },
                { key: "master120", label: "Large · 2-minute master cinematic" },
              ].map(({ key, label }) => (
                <Row key={key} label={label}>
                  <PriceInput
                    value={state.standaloneVideo?.heroPrices?.[key] ?? 0}
                    onChange={(v) => update("standaloneVideo", { heroPrices: { ...(state.standaloneVideo?.heroPrices || {}), [key]: v } })}
                    testId={`admin-standalone-video-hero-${key}`}
                  />
                </Row>
              ))}
            </div>
            <div className="mb-5">
              <p className="text-[10.5px] uppercase tracking-[0.12em] text-white/40 mb-2">Large-tier add-ons</p>
              <Row label="20-second unit interior video" hint="Per typical unit layout · new animation">
                <PriceInput
                  value={state.standaloneVideo?.addOnPrices?.unitInterior ?? 0}
                  onChange={(v) => update("standaloneVideo", { addOnPrices: { ...(state.standaloneVideo?.addOnPrices || {}), unitInterior: v } })}
                  testId="admin-standalone-video-addon-unit"
                />
              </Row>
              <Row label="30-second penthouse / signature video" hint="Each · new animation">
                <PriceInput
                  value={state.standaloneVideo?.addOnPrices?.penthouse ?? 0}
                  onChange={(v) => update("standaloneVideo", { addOnPrices: { ...(state.standaloneVideo?.addOnPrices || {}), penthouse: v } })}
                  testId="admin-standalone-video-addon-penthouse"
                />
              </Row>
              <Row label="Social cut-down (per clip after included)" hint="Editing only — never priced as animation.">
                <PriceInput
                  value={state.standaloneVideo?.addOnPrices?.socialCutdownPerClip ?? 0}
                  onChange={(v) => update("standaloneVideo", { addOnPrices: { ...(state.standaloneVideo?.addOnPrices || {}), socialCutdownPerClip: v } })}
                  testId="admin-standalone-video-addon-social"
                />
              </Row>
              <Row label="Social cut-downs included free" hint="Bundled free with a master before the per-clip rate applies.">
                <QtyInput
                  value={state.standaloneVideo?.socialCutdownsIncluded ?? 0}
                  onChange={(v) => update("standaloneVideo", { socialCutdownsIncluded: v })}
                  testId="admin-standalone-video-social-included"
                />
              </Row>
            </div>
          </div>
        </SectionCard>

        {/* SECTION 8 — FLOOR PLANS */}
        <SectionCard title="8 · Floor Plans" subtitle="Flat per-plan rates. Quantity is driven by the unique floor plan / home model count from Phase 1.">
          <div className="mb-5">
            <p className="text-[11px] uppercase tracking-[0.14em] text-white/55 mb-3">8A · Per-Plan Rates</p>
            <Row label="2D Floor Plan (CAD, vectorized)" hint="Per plan, flat rate. Not volume-tiered until thresholds below.">
              <PriceInput
                value={state.floorPlans?.price2D ?? 0}
                onChange={(v) => update("floorPlans", { price2D: v })}
                testId="admin-fp-2d"
              />
            </Row>
            <Row label="3D Floor Plan — AI Hybrid (base rate)" hint="Per plan, flat rate for the AI Hybrid workflow (1 revision round). Not volume-tiered until thresholds below.">
              <PriceInput
                value={state.floorPlans?.price3D ?? 0}
                onChange={(v) => update("floorPlans", { price3D: v })}
                testId="admin-fp-3d"
              />
            </Row>
            <Row label="3D Floor Plan — Traditional pipeline upcharge" hint="Added on top of the AI Hybrid rate when the client chooses the full no-AI 3D pipeline (2 revision rounds, exact to spec).">
              <PriceInput
                value={state.floorPlans?.traditional3DUpcharge ?? 500}
                onChange={(v) => update("floorPlans", { traditional3DUpcharge: v })}
                testId="admin-fp-3d-traditional-upcharge"
              />
            </Row>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.14em] text-white/55 mb-3">8B · Volume Discount Thresholds</p>
            <Row label="Tier 1 — minimum quantity" hint="Quantity at which Tier 1 volume discount kicks in.">
              <QtyInput
                value={state.floorPlans?.tier1MinQty ?? 6}
                onChange={(v) => update("floorPlans", { tier1MinQty: v })}
                testId="admin-fp-tier1-min"
              />
            </Row>
            <Row label="Tier 1 discount %">
              <div className="flex items-center gap-1.5">
                <PriceInput
                  value={state.floorPlans?.tier1Pct ?? 5}
                  onChange={(v) => update("floorPlans", { tier1Pct: v })}
                  testId="admin-fp-tier1-pct"
                  prefix=""
                />
                <span className="text-[11px] text-white/40">%</span>
              </div>
            </Row>
            <Row label="Tier 2 — minimum quantity" hint="Quantity at which Tier 2 (higher) volume discount kicks in.">
              <QtyInput
                value={state.floorPlans?.tier2MinQty ?? 11}
                onChange={(v) => update("floorPlans", { tier2MinQty: v })}
                testId="admin-fp-tier2-min"
              />
            </Row>
            <Row label="Tier 2 discount %">
              <div className="flex items-center gap-1.5">
                <PriceInput
                  value={state.floorPlans?.tier2Pct ?? 10}
                  onChange={(v) => update("floorPlans", { tier2Pct: v })}
                  testId="admin-fp-tier2-pct"
                  prefix=""
                />
                <span className="text-[11px] text-white/40">%</span>
              </div>
            </Row>
            <p className="text-[11px] text-white/45 italic mt-3 leading-snug">
              Discounts apply independently to 2D and 3D quantities. Plans are not pooled for discount qualification.
            </p>
          </div>
        </SectionCard>

        {/* SECTION 8B — ADDITIONAL REVISIONS & ON-SITE TRAVEL */}
        <SectionCard
          title="8B · Additional Revisions & On-Site Travel"
          subtitle="Add-on pricing surfaced in the calculator sidebar. Revisions are a percentage of the net visualization scope; travel is a flat per-day rate."
        >
          <p className="text-[11px] uppercase tracking-[0.16em] text-[#8FA79B]/70 mb-2">Additional Revision Rounds</p>
          <Row label="Included revision rounds" hint="Rounds bundled into the standard package at no extra charge.">
            <QtyInput
              value={state.revisions?.includedRounds ?? ADMIN_DEFAULTS.revisions.includedRounds}
              onChange={(v) => update("revisions", { includedRounds: v })}
              testId="admin-revisions-included"
            />
          </Row>
          <Row label="Cost per additional round (% of viz scope)" hint="e.g. 10% of a $30,000 net visualization scope = $3,000 per extra round.">
            <PriceInput
              value={state.revisions?.additionalPct ?? ADMIN_DEFAULTS.revisions.additionalPct}
              onChange={(v) => update("revisions", { additionalPct: v })}
              testId="admin-revisions-pct"
              prefix="%"
            />
          </Row>

          <p className="text-[11px] uppercase tracking-[0.16em] text-[#8FA79B]/70 mb-2 mt-6">On-Site Travel</p>
          <Row label="Travel rate per day" hint="Flat fee to send our team on-site, per day.">
            <PriceInput
              value={state.travel?.perDayRate ?? ADMIN_DEFAULTS.travel.perDayRate}
              onChange={(v) => update("travel", { perDayRate: v })}
              testId="admin-travel-per-day"
            />
          </Row>
          <Row label="Maximum selectable days" hint="Upper limit the rep can select in the calculator.">
            <QtyInput
              value={state.travel?.maxDays ?? ADMIN_DEFAULTS.travel.maxDays}
              onChange={(v) => update("travel", { maxDays: v })}
              testId="admin-travel-max-days"
            />
          </Row>
        </SectionCard>

        {/* SECTION 8C — DISCOUNT GUARDRAILS & COMMISSION POLICY */}
        <SectionCard
          title="8C · Discount Guardrails & Commission Policy"
          subtitle="Internal only. How far below the rate card a rep may price before the calculator flags it: green inside the first step, amber up to the floor (needs approval), red below the floor. Warns, never blocks. Nothing here reaches the client or changes commission math. The bundle incentive (Section 6) is the rep's pre-approved first lever and is never flagged; the additional discount below is measured on what is left after it."
        >
          <p className="text-[11px] uppercase tracking-[0.16em] text-[#8FA79B]/70 mb-2">Per-Line Price Overrides — % off the calculated price</p>
          <GuardrailLinesEditor
            lines={state.discountGuardrails?.lines}
            onChange={(next) => update("discountGuardrails", { lines: next })}
          />

          <p className="text-[11px] uppercase tracking-[0.16em] text-[#8FA79B]/70 mb-2 mt-6">Additional Proposal Discount — % of the one-time scope</p>
          <Row label="First step (free)" hint="A rep may take this much off the whole proposal without approval.">
            <PriceInput
              value={state.discountGuardrails?.additionalDiscountFirstStepPct ?? ADMIN_DEFAULTS.discountGuardrails.additionalDiscountFirstStepPct}
              onChange={(v) => update("discountGuardrails", { additionalDiscountFirstStepPct: v })}
              testId="admin-guardrail-addl-step"
              prefix="%"
            />
          </Row>
          <Row label="Floor (max)" hint="Past this the deal is below the floor.">
            <PriceInput
              value={state.discountGuardrails?.additionalDiscountFloorPct ?? ADMIN_DEFAULTS.discountGuardrails.additionalDiscountFloorPct}
              onChange={(v) => update("discountGuardrails", { additionalDiscountFloorPct: v })}
              testId="admin-guardrail-addl-floor"
              prefix="%"
            />
          </Row>

          <p className="text-[11px] uppercase tracking-[0.16em] text-[#8FA79B]/70 mb-2 mt-6">Commission Policy</p>
          <Row label="Approval required between first step and floor" hint="Off: the amber flag still shows, without the word approval.">
            <input
              type="checkbox"
              checked={state.discountGuardrails?.approvalRequiredBetweenStepAndFloor !== false}
              onChange={(e) => update("discountGuardrails", { approvalRequiredBetweenStepAndFloor: e.target.checked })}
              className="accent-[#8FA79B] w-4 h-4"
              data-testid="admin-guardrail-approval-required"
            />
          </Row>
          <div className="py-2.5">
            <p className="text-[13px] text-white/85">Below-the-floor commission rule</p>
            <p className="text-[10.5px] text-white/35 mt-0.5 mb-2">Shown to the rep on every red flag. Commissions are still entered by hand on the project.</p>
            <textarea
              rows={2}
              value={state.discountGuardrails?.belowFloorCommissionRule ?? ""}
              onChange={(e) => update("discountGuardrails", { belowFloorCommissionRule: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-md px-2.5 py-2 text-white text-[12.5px] outline-none focus:border-[#8FA79B]/50 leading-snug"
              data-testid="admin-guardrail-commission-rule"
            />
          </div>
        </SectionCard>

        {/* SECTION 8D — COMPLIMENTARY ADD-ONS */}
        <SectionCard
          title="8D · Complimentary Add-Ons"
          subtitle="What a rep may give at no charge. Each prints on the proposal as an “Included” line with its value; none changes a total or a payment. The running value is capped at a % of the quote's one-time contract value."
        >
          <Row label="Cap (% of one-time contract value)" hint="e.g. 5% of a $100,000 one-time scope = up to $5,000 of add-ons. COO / Admin may exceed it with a confirm.">
            <PriceInput
              value={state.freeAddOns?.capPctOfContract ?? ADMIN_DEFAULTS.freeAddOns.capPctOfContract}
              onChange={(v) => update("freeAddOns", { capPctOfContract: v })}
              testId="admin-free-addons-cap"
              prefix="%"
            />
          </Row>
          <p className="text-[11px] uppercase tracking-[0.16em] text-[#8FA79B]/70 mb-2 mt-6">Add-Ons — value per unit</p>
          <FreeAddOnsEditor
            items={state.freeAddOns?.items}
            onChange={(next) => update("freeAddOns", { items: next })}
          />
        </SectionCard>

        {/* ── 9 · MARKETING STRATEGY & LEAD GENERATION ── */}
        <SectionCard title="9 · Marketing Strategy & Lead Generation" subtitle="Campaign fees, CPL tiers, and slider defaults for the Marketing & Collateral tab.">
          <p className="text-[11px] uppercase tracking-[0.16em] text-[#8FA79B]/70 mb-2">9A · Campaign Fees — Tiered by Unit/Lot Count</p>
          <p className="text-[11.5px] text-white/45 mb-4 -mt-1">Tier looked up against the project unit/lot count entered in Phase 1.</p>
          <CampaignFeeTiersEditor
            tiers={state.marketing?.campaignFeeTiers || MARKETING_DEFAULTS.campaignFeeTiers}
            onChange={(next) => update("marketing", { campaignFeeTiers: next })}
            testIdPrefix="admin-mkt-tier"
          />

          <p className="text-[11px] uppercase tracking-[0.16em] text-[#8FA79B]/70 mb-2 mt-6">9B · CPL Tiers — Pre-Sale</p>
          <CPLEditor
            tiers={state.marketing?.presaleCpl || [
              { id: "tier1", label: "Below $600K",   upperBound: 600000,   rate: 35  },
              { id: "tier2", label: "$600K – $1.2M", upperBound: 1200000,  rate: 60  },
              { id: "tier3", label: "$1.2M – $2.5M", upperBound: 2500000,  rate: 75  },
              { id: "tier4", label: "$2.5M+",        upperBound: null,     rate: 100 },
            ]}
            onChange={(next) => update("marketing", { presaleCpl: next })}
            unitLabel="$"
            testIdPrefix="admin-mkt-presale"
          />

          <p className="text-[11px] uppercase tracking-[0.16em] text-[#8FA79B]/70 mb-2 mt-6">9C · CPL Tiers — Pre-Lease</p>
          <CPLEditor
            tiers={state.marketing?.preleaseCpl || [
              { id: "tier1", label: "Below $1,500/mo",     upperBound: 1500, rate: 10 },
              { id: "tier2", label: "$1,500 – $2,500/mo",  upperBound: 2500, rate: 15 },
              { id: "tier3", label: "$2,500 – $4,500/mo",  upperBound: 4500, rate: 25 },
              { id: "tier4", label: "$4,500+/mo",          upperBound: null, rate: 40 },
            ]}
            onChange={(next) => update("marketing", { preleaseCpl: next })}
            unitLabel="$/mo"
            testIdPrefix="admin-mkt-prelease"
          />

          <p className="text-[11px] uppercase tracking-[0.16em] text-[#8FA79B]/70 mb-2 mt-6">9D · Slider Defaults</p>
          <Row label="Default units"><QtyInput value={state.marketing?.unitsDefault ?? 50} onChange={(v) => update("marketing", { unitsDefault: v })} testId="admin-mkt-units-default" /></Row>
          <Row label="Units slider min"><QtyInput value={state.marketing?.unitsMin ?? 4} onChange={(v) => update("marketing", { unitsMin: v })} testId="admin-mkt-units-min" /></Row>
          <Row label="Units slider max"><QtyInput value={state.marketing?.unitsMax ?? 5000} onChange={(v) => update("marketing", { unitsMax: v })} testId="admin-mkt-units-max" /></Row>
          <Row label="Default timeline (months)"><QtyInput value={state.marketing?.timelineDefaultMonths ?? 12} onChange={(v) => update("marketing", { timelineDefaultMonths: v })} testId="admin-mkt-timeline-default" /></Row>
          <Row label="Timeline slider min (months)"><QtyInput value={state.marketing?.timelineMinMonths ?? 1} onChange={(v) => update("marketing", { timelineMinMonths: v })} testId="admin-mkt-timeline-min" /></Row>
          <Row label="Timeline slider max (months)"><QtyInput value={state.marketing?.timelineMaxMonths ?? 60} onChange={(v) => update("marketing", { timelineMaxMonths: v })} testId="admin-mkt-timeline-max" /></Row>
          <Row label="Default conversion rate (%)">
            <div className="flex items-center gap-1.5">
              <PriceInput value={state.marketing?.conversionDefaultPct ?? 1.5} onChange={(v) => update("marketing", { conversionDefaultPct: v })} testId="admin-mkt-conv-default" prefix="" />
              <span className="text-[11px] text-white/40">%</span>
            </div>
          </Row>
        </SectionCard>

        {/* ── 10 · COLLATERAL SUITE ── */}
        <SectionCard title="10 · Collateral Suite" subtitle="One-time deliverable prices and AI Social Media Retainer monthly rates.">
          <p className="text-[11px] uppercase tracking-[0.16em] text-[#8FA79B]/70 mb-2">10A · Digital Presence</p>
          <Row label="Landing Page"><PriceInput value={state.collateral?.landingPage ?? 3000} onChange={(v) => update("collateral", { landingPage: v })} testId="admin-col-landing" /></Row>
          <Row label="Full Website"><PriceInput value={state.collateral?.fullWebsite ?? 7500} onChange={(v) => update("collateral", { fullWebsite: v })} testId="admin-col-website" /></Row>

          <p className="text-[11px] uppercase tracking-[0.16em] text-[#8FA79B]/70 mb-2 mt-6">10B · Branding & Positioning</p>
          <Row label="Branding & Conceptual Package"><PriceInput value={state.collateral?.brandingPackage ?? 10000} onChange={(v) => update("collateral", { brandingPackage: v })} testId="admin-col-branding" /></Row>
          <Row label="Investment Presentation"><PriceInput value={state.collateral?.investmentPresentation ?? 4000} onChange={(v) => update("collateral", { investmentPresentation: v })} testId="admin-col-investment" /></Row>

          <p className="text-[11px] uppercase tracking-[0.16em] text-[#8FA79B]/70 mb-2 mt-6">10C · Sales & Marketing Collateral</p>
          <Row label="Development Flipbook"><PriceInput value={state.collateral?.developmentFlipbook ?? 7500} onChange={(v) => update("collateral", { developmentFlipbook: v })} testId="admin-col-flipbook" /></Row>
          <Row label="Billboard Design (Static)"><PriceInput value={state.collateral?.billboardStatic ?? 1500} onChange={(v) => update("collateral", { billboardStatic: v })} testId="admin-col-billboard-static" /></Row>
          <Row label="Billboard Design (Animated)"><PriceInput value={state.collateral?.billboardAnimated ?? 3000} onChange={(v) => update("collateral", { billboardAnimated: v })} testId="admin-col-billboard-animated" /></Row>
          <Row label="Unit Inventory Breakdown Sheet"><PriceInput value={state.collateral?.unitInventorySheet ?? 750} onChange={(v) => update("collateral", { unitInventorySheet: v })} testId="admin-col-unit-inv" /></Row>

          <p className="text-[11px] uppercase tracking-[0.16em] text-[#8FA79B]/70 mb-2 mt-6">10D · AI-Enhanced Content</p>
          <Row label="Animated Renders (per item)"><PriceInput value={state.collateral?.animatedRendersPer ?? 899} onChange={(v) => update("collateral", { animatedRendersPer: v })} testId="admin-col-animated-renders" /></Row>
          <Row label="Render Enhancement (per item)"><PriceInput value={state.collateral?.renderEnhancementPer ?? 599} onChange={(v) => update("collateral", { renderEnhancementPer: v })} testId="admin-col-render-enhancement" /></Row>

          <p className="text-[11px] uppercase tracking-[0.16em] text-[#8FA79B]/70 mb-2 mt-6">10E · AI Social Media Retainer Packages</p>
          <Row label="6 clips / month"><PriceInput value={state.collateral?.retainer6 ?? 5394} onChange={(v) => update("collateral", { retainer6: v })} testId="admin-col-retainer-6" /></Row>
          <Row label="12 clips / month (10% volume)"><PriceInput value={state.collateral?.retainer12 ?? 9708} onChange={(v) => update("collateral", { retainer12: v })} testId="admin-col-retainer-12" /></Row>
          <Row label="18 clips / month (15% volume)"><PriceInput value={state.collateral?.retainer18 ?? 13755} onChange={(v) => update("collateral", { retainer18: v })} testId="admin-col-retainer-18" /></Row>
        </SectionCard>
      </div>

      {/* Sticky footer with Save / Reset */}
      <footer className="fixed bottom-0 left-0 right-0 z-[81] bg-[#000000]/95 backdrop-blur border-t border-white/[0.08] py-4">
        <div className="max-w-5xl mx-auto px-8 lg:px-12 flex items-center justify-between gap-4">
          <div className="text-[12px] text-white/55 flex flex-col gap-1">
            {saveError ? (
              <span className="inline-flex items-center gap-1.5 text-red-300" data-testid="pricing-admin-save-error">
                <AlertTriangle className="w-3.5 h-3.5" /> {saveError}
              </span>
            ) : justSaved ? (
              <span className="inline-flex items-center gap-1.5 text-emerald-300" data-testid="pricing-admin-save-success">
                <CheckCircle2 className="w-3.5 h-3.5" /> Saved to {audienceCount} team member{audienceCount === 1 ? "" : "s"}.
              </span>
            ) : dirty ? (
              <span className="inline-flex items-center gap-1.5 text-amber-300/90">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-300/90"></span> Unsaved changes
              </span>
            ) : (
              <span className="text-white/30">All changes synced</span>
            )}
          </div>
          <div className="flex items-center gap-2.5">
            <button
              onClick={handleReset}
              disabled={saving}
              className="px-4 py-2 rounded-md text-[12px] uppercase tracking-[0.12em] text-white/65 hover:text-white border border-white/15 hover:border-white/30 transition-colors inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="admin-reset"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset to Defaults
            </button>
            <button
              onClick={handleSave}
              disabled={!dirty || saving}
              className="px-5 py-2 rounded-md text-[12px] uppercase tracking-[0.12em] bg-[#8FA79B] text-[#000000] hover:bg-[#8FA79B]/90 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2 font-medium transition-colors"
              data-testid="admin-save"
            >
              <Save className="w-3.5 h-3.5" />
              {saving
                ? "Saving…"
                : audienceCount > 0
                  ? `Save for ${audienceCount} team member${audienceCount === 1 ? "" : "s"}`
                  : "Save Changes"}
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
